/** cspell:ignore sprd */
import { values } from 'lodash-es';
import { memo, useMemo, useRef, type ChangeEventHandler } from 'react';
import { defineMessages } from 'react-intl';
import { SecurityType, useMixpanel } from '../../contexts';
import { useConstant, useDynamicCallback, useIntl, useIsMobile } from '../../hooks';
import { MixpanelEvent, MixpanelEventProperty } from '../../tokens';
import { AccordionElement } from '../Accordion/AccordionElement';
import { ButtonVariants, IconButton } from '../Button';
import { Box, type BoxProps } from '../Core';
import { Divider } from '../Divider';
import { DropdownFilter, ToggleButtonFilter, type DropdownFilterProps, type ToggleButtonFilterProps } from '../Filters';
import { FormControlSizes, Input } from '../Form';
import { Icon, IconName } from '../Icons';
import { SearchWrapper, WatchListFiltersWrapper } from './styles';
import type { WatchlistLayout } from './types';

const messages = defineMessages({
  searchSymbol: {
    defaultMessage: 'Search Symbol...',
    id: 'Watchlist.searchSymbol',
  },
});

export interface WatchlistFiltersProps {
  filteredCurrencies: string[];
  onFilteredCurrenciesChange: (currencies: string[]) => void;
  securityType: SecurityType;
  onSecurityTypeChange?: (securityType: SecurityType) => void;
  showOnlyFavorites: boolean;
  onShowOnlyFavorites: (showOnlyFavorites: boolean) => void;
  onSearchTextChange: ChangeEventHandler<HTMLInputElement>;
  searchText: string;
  selectableCurrencies?: string[];
  layout: WatchlistLayout | undefined;
  securityTypesOptions?: SecurityType[];
}

export const FilterableProperties = {
  FAVORITES: 'Favorites',
  PRODUCT_TYPE: 'Product Type',
  CURRENCY: 'Currency',
} as const;
export type FilterableProperties = (typeof FilterableProperties)[keyof typeof FilterableProperties];

const DEFAULT_CURRENCIES = ['BTC', 'ETH', 'USD', 'USDT', 'EUR'];
const DEFAULT_LAYOUT: WatchlistLayout = {
  currency: 'show',
  favorites: 'show',
  securityType: 'show',
};

const securityTypeFilterOptions = values(SecurityType);

export const WatchlistFilters = memo(function ({
  filteredCurrencies,
  onFilteredCurrenciesChange,
  securityType,
  onSecurityTypeChange,
  showOnlyFavorites,
  onShowOnlyFavorites,
  onSearchTextChange,
  searchText,
  selectableCurrencies = DEFAULT_CURRENCIES,
  layout = DEFAULT_LAYOUT,
  securityTypesOptions = securityTypeFilterOptions,
}: WatchlistFiltersProps) {
  const mixpanel = useMixpanel();
  const isMobile = useIsMobile();

  const toggleOnlyFavorites = useDynamicCallback(() => {
    mixpanel.track(MixpanelEvent.FilterWatchlist, {
      [MixpanelEventProperty.Filter]: FilterableProperties.FAVORITES,
    });
    onShowOnlyFavorites(!showOnlyFavorites);
  });

  const dropdownItemProps = useConstant<DropdownFilterProps<SecurityType>['itemProps']>({
    size: FormControlSizes.Default,
  });

  const dropdownButtonProps = useConstant<DropdownFilterProps<SecurityType>['buttonProps']>({
    size: isMobile ? FormControlSizes.Default : FormControlSizes.Small,
    variant: ButtonVariants.Default,
    endIcon: undefined,
  });

  const dropdownFilterProps = useMemo(
    function DropdownFilterProps() {
      return {
        options: securityTypesOptions,
        renderItem: label => label,
        renderSelectedItem: (selected: SecurityType) =>
          selected === SecurityType.CalendarSpread ? 'C Sprd' : selected,
        getKey: label => label,
        onChange: option => {
          mixpanel.track(MixpanelEvent.FilterWatchlist, {
            [MixpanelEventProperty.Filter]: FilterableProperties.PRODUCT_TYPE,
            [MixpanelEventProperty.Value]: option,
          });
          onSecurityTypeChange?.(option);
        },
        selection: securityType,
        wrapperProps: {
          minWidth: isMobile ? '48px' : '42px',
        },
      } satisfies DropdownFilterProps<SecurityType>;
    },
    [mixpanel, securityType, onSecurityTypeChange, securityTypesOptions, isMobile]
  );

  const selectedCurrencies = useMemo(() => new Set(filteredCurrencies), [filteredCurrencies]);
  const toggleFilterProps = useMemo(
    function ToggleButtonFilterProps() {
      return {
        options: selectableCurrencies,
        selections: [...selectedCurrencies.values()],
        getKey: label => label,
        onToggleChange: newSelections => {
          mixpanel.track(MixpanelEvent.FilterWatchlist, {
            [MixpanelEventProperty.Filter]: FilterableProperties.CURRENCY,
          });
          onFilteredCurrenciesChange(newSelections);
        },
        renderContent: label => label,
        multiSelect: false,
      } satisfies Partial<ToggleButtonFilterProps<string>>;
    },
    [mixpanel, selectedCurrencies, onFilteredCurrenciesChange, selectableCurrencies]
  );

  const showFilters =
    !isMobile || layout.favorites !== 'hide' || layout.currency !== 'hide' || layout.securityType !== 'hide';
  const initialShowFilters = useRef(showFilters);

  return (
    <>
      <Box px="spacing12" pb="spacing12">
        <SearchComponent onSearchTextChange={onSearchTextChange} searchText={searchText} />
      </Box>
      <AccordionElement
        isOpen={showFilters}
        initialOpen={initialShowFilters.current}
        px="spacing12"
        gap="spacing12"
        display="flex"
        flexDirection="column"
        pb="spacing12"
        borderBottom="solid 1px"
        borderColor="borderColorTab"
      >
        <WatchListFiltersWrapper data-testid="watchlist-filters-wrapper">
          {(!isMobile || layout.favorites === 'show') && (
            <IconButton
              data-testid="toggle-only-favorites"
              onClick={toggleOnlyFavorites}
              size={isMobile ? FormControlSizes.Default : FormControlSizes.Small}
              icon={showOnlyFavorites ? IconName.StarSolid : IconName.Star}
              color={showOnlyFavorites ? 'yellow.lighten' : 'gray.080'}
            />
          )}
          {layout.securityType === 'show' && onSecurityTypeChange && (
            <DropdownFilter
              buttonProps={dropdownButtonProps}
              itemProps={dropdownItemProps}
              {...dropdownFilterProps}
              data-testid="watchlist-product-type-menu"
            />
          )}
          <Divider orientation="vertical" mr="spacing4" ml="spacing4" />
          {layout.currency === 'show' && (
            <ToggleButtonFilter
              size={isMobile ? FormControlSizes.Default : FormControlSizes.Small}
              {...toggleFilterProps}
            />
          )}
        </WatchListFiltersWrapper>
      </AccordionElement>
    </>
  );
});

interface SearchProps {
  onSearchTextChange: ChangeEventHandler<HTMLInputElement>;
  searchText: string;
}

function SearchComponent({ onSearchTextChange, searchText, ...props }: SearchProps & BoxProps) {
  const { formatMessage } = useIntl();
  return (
    <SearchWrapper {...props}>
      <Input
        data-testid="search-input"
        prefix={<Icon icon={IconName.Search} />}
        placeholder={formatMessage(messages.searchSymbol)}
        onChange={onSearchTextChange}
        value={searchText}
        clearable={true}
        size={FormControlSizes.Default}
      />
    </SearchWrapper>
  );
}
